<template>
  <div class="notification-create-page">
    <b-row>
      <b-col
        cols="12"
        lg="8"
      >
        <b-card
          title="Содержание"
          class="mb-2"
        >
          <b-row>
            <b-col cols="12">
              <b-form-group
                label="Шаблон"
                label-for="template-select"
                description="Выберите шаблон, чтобы заполнить заголовок и текст"
              >
                <b-form-select
                  id="template-select"
                  v-model="selectedTemplateId"
                  :options="templateOptions"
                  @change="applyTemplate"
                />
              </b-form-group>
            </b-col>
            <b-col
              v-if="selectedTemplateKind"
              cols="12"
            >
              <div class="template-kind-preview">
                <span class="template-kind-preview__icon">
                  <feather-icon
                    :icon="templateKindIcon(selectedTemplateKind)"
                    size="20"
                  />
                </span>
                <div>
                  <strong>{{ templateKindLabel(selectedTemplateKind) }}</strong>
                  <small class="text-muted d-block">Иконка, которую увидит пользователь в приложении</small>
                </div>
              </div>
            </b-col>
            <b-col cols="12">
              <b-form-group
                label="Заголовок"
                label-for="notif-title"
              >
                <b-form-input
                  id="notif-title"
                  v-model="form.title"
                  placeholder="Например: Напоминание об оплате"
                />
              </b-form-group>
            </b-col>
            <b-col cols="12">
              <b-form-group
                label="Текст уведомления"
                label-for="notif-text"
              >
                <b-form-textarea
                  id="notif-text"
                  v-model="form.text"
                  rows="5"
                  placeholder="Полный текст, который увидит пользователь"
                />
              </b-form-group>
            </b-col>
            <b-col
              v-if="requiresRelatedStart"
              cols="12"
            >
              <b-form-group
                label="Старт уведомления"
                label-for="related-start-select"
                description="Старт, к которому относится это уведомление. Пользователь перейдёт на его страницу по кнопке в приложении."
              >
                <v-select
                  id="related-start-select"
                  v-model="relatedStartOption"
                  :filterable="false"
                  :options="startSearchOptions"
                  :clearable="true"
                  label="label"
                  placeholder="Начните вводить название старта..."
                  class="start-picker-select"
                  @search="onStartSearch"
                  @input="onRelatedStartPicked"
                >
                  <template #no-options>
                    {{ startSearchHint }}
                  </template>
                  <template #option="option">
                    {{ option.label }}
                  </template>
                  <template #selected-option="option">
                    {{ option.label }}
                  </template>
                </v-select>
              </b-form-group>
            </b-col>
          </b-row>
        </b-card>

        <b-card
          title="Получатели"
          class="mb-2"
        >
          <b-form-group label="Кому отправить">
            <div class="recipient-options">
              <div
                v-for="option in recipientOptions"
                :key="option.value"
                class="recipient-option"
                :class="{ 'recipient-option--active': form.recipientType === option.value }"
                @click="form.recipientType = option.value"
              >
                <b-form-radio
                  v-model="form.recipientType"
                  :value="option.value"
                  class="recipient-option__radio"
                >
                  <span class="recipient-option__label">{{ option.label }}</span>
                </b-form-radio>
                <small class="recipient-option__hint text-muted">{{ option.hint }}</small>
              </div>
            </div>
          </b-form-group>

          <b-form-group
            v-if="isStartRecipient"
            label="Старт"
            label-for="start-select"
          >
            <b-form-select
              id="start-select"
              v-model="form.startId"
              :options="startOptions"
            />
          </b-form-group>

          <template v-if="form.recipientType === 'filter'">
            <b-form-group
              label="Условие выборки"
              label-for="filter-select"
            >
              <b-form-select
                id="filter-select"
                v-model="form.filter"
                :options="filterOptions"
              />
            </b-form-group>

            <b-form-group
              v-if="form.filter === 'no_review'"
              label="Старт"
              label-for="filter-start-select"
              description="Пользователи, участвовавшие в этом старте и не оставившие отзыв"
            >
              <b-form-select
                id="filter-start-select"
                v-model="form.filterStartId"
                :options="startOptions"
              />
            </b-form-group>
          </template>

          <b-form-group
            v-if="form.recipientType === 'user_ids'"
            label="Пользователи"
            label-for="user-picker"
            description="Начните вводить ФИО или ID — выберите из списка"
          >
            <v-select
              id="user-picker"
              v-model="userPicker"
              :filterable="false"
              :options="userSearchOptions"
              :clearable="true"
              placeholder="Поиск по ФИО или ID..."
              class="user-picker-select"
              @search="onUserSearch"
              @input="onUserPicked"
            >
              <template #no-options>
                {{ userSearchHint }}
              </template>
              <template #option="option">
                {{ option.displayName }}
              </template>
              <template #selected-option="option">
                {{ option.displayName }}
              </template>
            </v-select>

            <div
              v-if="selectedRecipients.length"
              class="selected-recipients mt-1"
            >
              <div class="selected-recipients__header">
                <strong>Выбрано: {{ selectedRecipients.length }}</strong>
              </div>
              <ul class="selected-recipients__list">
                <li
                  v-for="recipient in selectedRecipients"
                  :key="recipient.id"
                >
                  <span>{{ recipient.displayName }}</span>
                  <b-button
                    size="sm"
                    variant="flat-danger"
                    class="btn-icon"
                    @click="removeRecipient(recipient.id)"
                  >
                    <feather-icon
                      icon="XIcon"
                      size="14"
                    />
                  </b-button>
                </li>
              </ul>
            </div>
            <small
              v-else
              class="text-muted d-block mt-50"
            >
              Получатели не выбраны
            </small>
          </b-form-group>
        </b-card>

        <b-card
          title="Каналы доставки"
          class="mb-2"
        >
          <b-form-checkbox
            v-model="inAppChannel"
            disabled
            checked
          >
            Личный кабинет
          </b-form-checkbox>
          <small class="text-muted d-block mt-50">
            Telegram и push-уведомления будут доступны позже.
          </small>
        </b-card>
      </b-col>

      <b-col
        cols="12"
        lg="4"
      >
        <b-card
          title="Действия"
          class="actions-card sticky-actions"
        >
          <p class="text-muted small mb-1">
            Сначала проверьте количество получателей, затем отправьте уведомление.
          </p>

          <b-button
            v-ripple.400="'rgba(115, 103, 240, 0.15)'"
            variant="outline-primary"
            block
            class="mb-1"
            @click="previewRecipients"
          >
            <feather-icon
              icon="UsersIcon"
              size="16"
              class="mr-50"
            />
            Предпросмотр получателей
          </b-button>

          <b-button
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="primary"
            block
            :disabled="sending || !canSend"
            @click="sendNotification"
          >
            <b-spinner
              v-if="sending"
              small
              class="mr-50"
            />
            <feather-icon
              v-else
              icon="SendIcon"
              size="16"
              class="mr-50"
            />
            Отправить уведомление
          </b-button>

          <b-alert
            v-if="previewResult"
            show
            variant="info"
            class="mt-2 mb-0"
          >
            <strong>Найдено:</strong> {{ previewResult.totalFound }}

            <ul
              v-if="previewResult.recipients && previewResult.recipients.length"
              class="preview-recipients-list mb-0 mt-1"
            >
              <li
                v-for="recipient in previewResult.recipients"
                :key="recipient.id"
              >
                {{ recipient.displayName }}
              </li>
            </ul>

            <span
              v-if="previewResult.notFoundUserIds && previewResult.notFoundUserIds.length"
              class="d-block mt-50"
            >
              <strong>Не найдены ID:</strong> {{ previewResult.notFoundUserIds.join(', ') }}
            </span>
            <span
              v-if="previewResult.notFoundLines && previewResult.notFoundLines.length"
              class="d-block mt-50"
            >
              <strong>Не найдены:</strong> {{ previewResult.notFoundLines.join(', ') }}
            </span>
          </b-alert>

          <b-alert
            v-if="sendResult"
            show
            variant="success"
            class="mt-2 mb-0"
          >
            Отправлено в ЛК: <strong>{{ sendResult.inAppDeliveredCount }}</strong>
            <b-button
              v-if="sendResult"
              size="sm"
              variant="link"
              class="p-0 mt-50"
              @click="goToHistory"
            >
              Перейти в историю
            </b-button>
          </b-alert>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import _ from 'lodash'
import vSelect from 'vue-select'
import Ripple from 'vue-ripple-directive'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import {
  BAlert,
  BButton,
  BCard,
  BCol,
  BFormCheckbox,
  BFormGroup,
  BFormInput,
  BFormRadio,
  BFormSelect,
  BFormTextarea,
  BRow,
  BSpinner,
} from 'bootstrap-vue'
import { FILTER_OPTIONS, RECIPIENT_OPTIONS, templateKindRequiresStart } from './notifications.constants'
import { normalizeUser, showNotificationError, templateKindIcon, templateKindLabel } from './notifications.utils'

export default {
  name: 'NotificationCreateTab',
  components: {
    vSelect,
    BAlert,
    BButton,
    BCard,
    BCol,
    BFormCheckbox,
    BFormGroup,
    BFormInput,
    BFormRadio,
    BFormSelect,
    BFormTextarea,
    BRow,
    BSpinner,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      sending: false,
      inAppChannel: true,
      selectedTemplateId: null,
      selectedTemplateKind: null,
      templates: [],
      starts: [],
      previewResult: null,
      sendResult: null,
      userPicker: null,
      relatedStartOption: null,
      userSearchOptions: [],
      userSearchHint: 'Введите минимум 2 символа или ID',
      startSearchOptions: [],
      startSearchHint: 'Введите минимум 2 символа',
      selectedRecipients: [],
      form: {
        title: '',
        text: '',
        recipientType: 'all',
        startId: null,
        filter: 'incomplete_profile',
        filterStartId: null,
        relatedStartId: null,
      },
      recipientOptions: RECIPIENT_OPTIONS,
      filterOptions: FILTER_OPTIONS,
    }
  },
  computed: {
    isStartRecipient() {
      return ['start_registered', 'start_paid', 'start_participated'].includes(this.form.recipientType)
    },
    requiresRelatedStart() {
      return templateKindRequiresStart(this.selectedTemplateKind)
    },
    effectiveRelatedStartId() {
      if (this.form.relatedStartId) {
        return Number(this.form.relatedStartId)
      }
      if (this.isStartRecipient && this.form.startId) {
        return Number(this.form.startId)
      }
      if (this.form.recipientType === 'filter' && this.form.filter === 'no_review' && this.form.filterStartId) {
        return Number(this.form.filterStartId)
      }
      return null
    },
    templateOptions() {
      return [
        { value: null, text: '— Без шаблона —' },
        ...this.templates.filter(t => t.isActive).map(t => ({ value: t.id, text: t.name })),
      ]
    },
    startOptions() {
      return [
        { value: null, text: '— Выберите старт —' },
        ...this.starts.map(s => ({ value: s.id, text: s.name })),
      ]
    },
    canSend() {
      const hasContent = Boolean(this.form.title?.trim() && this.form.text?.trim())
      if (this.requiresRelatedStart && !this.effectiveRelatedStartId) {
        return false
      }
      if (this.form.recipientType === 'user_ids') {
        return hasContent && this.selectedRecipients.length > 0
      }
      return hasContent
    },
  },
  watch: {
    'form.startId'(startId) {
      if (this.requiresRelatedStart && startId && !this.form.relatedStartId) {
        this.form.relatedStartId = startId
        this.setRelatedStartOption(startId)
      }
    },
    'form.filterStartId'(startId) {
      if (this.requiresRelatedStart && startId && !this.form.relatedStartId) {
        this.form.relatedStartId = startId
        this.setRelatedStartOption(startId)
      }
    },
    selectedTemplateKind(kind) {
      if (!templateKindRequiresStart(kind)) {
        this.form.relatedStartId = null
        this.relatedStartOption = null
      }
    },
  },
  async mounted() {
    await Promise.all([this.loadTemplates(), this.loadStarts()])
  },
  methods: {
    templateKindIcon,
    templateKindLabel,
    buildPayload() {
      const payload = {
        title: this.form.title,
        text: this.form.text,
        recipientType: this.form.recipientType,
        channels: ['in_app'],
      }
      if (this.selectedTemplateKind) {
        payload.templateKind = this.selectedTemplateKind
      }
      if (this.effectiveRelatedStartId) {
        payload.relatedStartId = this.effectiveRelatedStartId
      }
      if (this.isStartRecipient && this.form.startId) {
        payload.startId = Number(this.form.startId)
      }
      if (this.form.recipientType === 'filter') {
        payload.filter = this.form.filter
        if (this.form.filter === 'no_review' && this.form.filterStartId) {
          payload.filterStartId = Number(this.form.filterStartId)
        }
      }
      if (this.form.recipientType === 'user_ids') {
        payload.userIds = this.selectedRecipients.map(r => r.id)
      }
      return payload
    },
    formatStartOption(start) {
      const date = start.start_date ? ` (${this.$moment(start.start_date).format('DD.MM.YYYY')})` : ''
      return {
        id: start.id,
        label: `${start.name}${date}`,
      }
    },
    setRelatedStartOption(startId) {
      const start = this.starts.find(s => Number(s.id) === Number(startId))
      this.relatedStartOption = start ? this.formatStartOption(start) : this.relatedStartOption
    },
    onStartSearch(search, loading) {
      const query = (search || '').trim()
      if (!query || query.length < 2) {
        this.startSearchOptions = this.starts.map(this.formatStartOption)
        this.startSearchHint = 'Введите минимум 2 символа'
        return
      }

      loading(true)
      this.searchStarts(query, loading)
    },
    searchStarts: _.debounce(async function searchStartsDebounced(search, loading) {
      try {
        const { data } = await this.$http.get('start/autocomplete', {
          params: { query: search, limit: 20 },
        })
        this.startSearchOptions = (data.suggestions || []).map(this.formatStartOption)
        this.startSearchHint = this.startSearchOptions.length ? '' : 'Старты не найдены'
      } catch (e) {
        this.startSearchOptions = []
        this.startSearchHint = 'Ошибка поиска'
      } finally {
        loading(false)
      }
    }, 350),
    onRelatedStartPicked(start) {
      this.form.relatedStartId = start?.id || null
      this.previewResult = null
    },
    onUserSearch(search, loading) {
      const query = (search || '').trim()
      if (!query) {
        this.userSearchOptions = []
        this.userSearchHint = 'Введите минимум 2 символа или ID'
        return
      }

      if (query.length < 2 && !/^\d+$/.test(query)) {
        this.userSearchOptions = []
        this.userSearchHint = 'Введите минимум 2 символа или ID'
        return
      }

      loading(true)
      this.searchUsers(query, loading)
    },
    searchUsers: _.debounce(async function searchUsersDebounced(search, loading) {
      const options = []
      const seen = new Set()

      const pushUser = user => {
        if (!user?.id || seen.has(user.id)) return
        seen.add(user.id)
        options.push(normalizeUser(user))
      }

      try {
        if (/^\d+$/.test(search)) {
          try {
            const { data } = await this.$http.get(`user/${search}`)
            pushUser(data)
          } catch (e) {
            // ID not found — still search by name below
          }
        }

        const { data } = await this.$http.get('user/virtual-payment/wallets', {
          params: { filterText: search, maxResultCount: 20 },
        })
        ;(data.rows || []).forEach(pushUser)

        this.userSearchOptions = options
        this.userSearchHint = options.length ? '' : 'Пользователи не найдены'
      } catch (e) {
        this.userSearchOptions = []
        this.userSearchHint = 'Ошибка поиска'
      } finally {
        loading(false)
      }
    }, 350),
    onUserPicked(user) {
      if (!user?.id) return

      if (!this.selectedRecipients.some(r => r.id === user.id)) {
        this.selectedRecipients.push(normalizeUser(user))
      }

      this.$nextTick(() => {
        this.userPicker = null
      })
      this.previewResult = null
    },
    removeRecipient(userId) {
      this.selectedRecipients = this.selectedRecipients.filter(r => r.id !== userId)
      this.previewResult = null
    },
    async previewRecipients() {
      if (this.form.recipientType === 'user_ids' && !this.selectedRecipients.length) {
        showNotificationError(this, { message: 'Выберите хотя бы одного пользователя' })
        return
      }
      try {
        const { data } = await this.$http.post('notifications/preview-recipients', this.buildPayload())
        this.previewResult = data
        this.sendResult = null
      } catch (e) {
        showNotificationError(this, e)
      }
    },
    async sendNotification() {
      if (this.requiresRelatedStart && !this.effectiveRelatedStartId) {
        showNotificationError(this, { message: 'Укажите старт уведомления для выбранного типа шаблона' })
        return
      }
      this.sending = true
      this.sendResult = null
      try {
        const { data } = await this.$http.post('notifications/send', this.buildPayload())
        this.sendResult = data
        this.previewResult = null
        this.$toast({
          component: ToastificationContent,
          props: { title: 'Уведомление отправлено', icon: 'CheckIcon', variant: 'success' },
        })
        this.$emit('sent')
      } catch (e) {
        showNotificationError(this, e)
      } finally {
        this.sending = false
      }
    },
    async loadTemplates() {
      const { data } = await this.$http.get('notification-templates', { params: { maxResultCount: 100 } })
      this.templates = data.rows || []
    },
    async loadStarts() {
      const { data } = await this.$http.get('start', { params: { maxResultCount: 200, sorting: 'start_date DESC' } })
      this.starts = data.rows || []
      this.startSearchOptions = this.starts.map(this.formatStartOption)
    },
    applyTemplate() {
      const template = this.templates.find(t => t.id === this.selectedTemplateId)
      if (template) {
        this.form.title = template.title
        this.form.text = template.text
        this.selectedTemplateKind = template.templateKind || null
      } else {
        this.selectedTemplateKind = null
      }
    },
    applyTemplateData(template) {
      if (!template) return

      this.selectedTemplateId = template.id
      this.form.title = template.title
      this.form.text = template.text
      this.selectedTemplateKind = template.templateKind || null
    },
    goToHistory() {
      this.$emit('open-history')
    },
  },
}
</script>

<style scoped>
.template-kind-preview {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 1rem;
  padding: 12px 14px;
  border: 1px solid #eeeef0;
  border-radius: 14px;
  background: #f7fdff;
}

.template-kind-preview__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 14px;
  background: #d9f5ff;
  color: #00baff;
  flex-shrink: 0;
}
</style>
